import { useState, useEffect } from 'react';
import type { DynamicPlan } from '@/mocks/superAdminPlans';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface PlanFormModalProps {
  plan: DynamicPlan | null;
  onClose: () => void;
  onSave: (plan: DynamicPlan) => void;
}

const storagePresets = [
  { label: '1 GB', value: 1 },
  { label: '5 GB', value: 5 },
  { label: '10 GB', value: 10 },
  { label: '25 GB', value: 25 },
  { label: '50 GB', value: 50 },
  { label: '100 GB', value: 100 },
  { label: '250 GB', value: 250 },
  { label: '500 GB', value: 500 },
  { label: '1 TB', value: 1024 },
];

function generateId(): string {
  return `plan_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
}

const colorOptions = [
  { value: '#78716c', label: 'Gris', ring: 'ring-stone-500' },
  { value: '#0ea5e9', label: 'Celeste', ring: 'ring-sky-500' },
  { value: '#d97706', label: 'Ámbar', ring: 'ring-amber-500' },
  { value: '#7c3aed', label: 'Violeta', ring: 'ring-violet-500' },
  { value: '#1c1917', label: 'Negro', ring: 'ring-foreground-900' },
  { value: '#dc2626', label: 'Rojo', ring: 'ring-red-500' },
  { value: '#16a34a', label: 'Verde', ring: 'ring-emerald-500' },
  { value: '#e11d48', label: 'Rosa', ring: 'ring-rose-500' },
  { value: '#059669', label: 'Teal', ring: 'ring-teal-500' },
  { value: '#c2410c', label: 'Naranja', ring: 'ring-orange-500' },
];

interface NumberField {
  value: number | null;
  unlimited: boolean;
}

function parseNumberField(val: number | null): NumberField {
  return { value: val === null ? 0 : val, unlimited: val === null };
}

function serializeNumberField(field: NumberField): number | null {
  return field.unlimited ? null : field.value;
}

export default function PlanFormModal({ plan, onClose, onSave }: PlanFormModalProps) {
  const isEditing = plan !== null;

  const [name, setName] = useState(plan?.name ?? '');
  const [priceMonthly, setPriceMonthly] = useState(plan?.priceMonthly ?? 0);
  const [priceAnnual, setPriceAnnual] = useState(plan?.priceAnnual ?? 0);
  const [trialDays, setTrialDays] = useState(plan?.trialDays ?? 0);
  const [storageGB, setStorageGB] = useState(plan?.storageGB ?? 10);
  const [locutores, setLocutores] = useState<NumberField>(parseNumberField(plan?.locutores ?? null));
  const [programs, setPrograms] = useState<NumberField>(parseNumberField(plan?.programs ?? null));
  const [events, setEvents] = useState<NumberField>(parseNumberField(plan?.events ?? null));
  const [lives, setLives] = useState<NumberField>(parseNumberField(plan?.lives ?? null));
  const [notifications, setNotifications] = useState<NumberField>(parseNumberField(plan?.notifications ?? null));
  const [frequencyPreferred, setFrequencyPreferred] = useState(plan?.frequencyPreferred ?? false);
  const [verification, setVerification] = useState(plan?.verification ?? false);
  const [customDomain, setCustomDomain] = useState(plan?.customDomain ?? false);
  const [apps, setApps] = useState(plan?.apps ?? false);
  const [active, setActive] = useState(plan?.active ?? true);
  const [visible, setVisible] = useState(plan?.visible ?? true);
  const [color, setColor] = useState(plan?.color ?? '#78716c');
  const [showCustomStorage, setShowCustomStorage] = useState(false);
  const [customStorage, setCustomStorage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setName(plan.name);
      setPriceMonthly(plan.priceMonthly);
      setPriceAnnual(plan.priceAnnual);
      setTrialDays(plan.trialDays);
      setStorageGB(plan.storageGB);
      setLocutores(parseNumberField(plan.locutores));
      setPrograms(parseNumberField(plan.programs));
      setEvents(parseNumberField(plan.events));
      setLives(parseNumberField(plan.lives));
      setNotifications(parseNumberField(plan.notifications));
      setFrequencyPreferred(plan.frequencyPreferred);
      setVerification(plan.verification);
      setCustomDomain(plan.customDomain);
      setApps(plan.apps);
      setActive(plan.active);
      setVisible(plan.visible);
      setColor(plan.color);
    }
  }, [plan]);

  const handleStoragePreset = (gb: number) => {
    setStorageGB(gb);
    setShowCustomStorage(false);
  };

  const handleCustomStorage = () => {
    const val = parseInt(customStorage, 10);
    if (val > 0) {
      setStorageGB(val);
      setShowCustomStorage(false);
      setCustomStorage('');
    }
  };

  const autoAnnual = () => {
    const val = Math.round(priceMonthly * 10);
    setPriceAnnual(val);
  };

  const handleNumberChange = (field: NumberField, setField: (v: NumberField) => void, raw: string) => {
    const num = parseInt(raw, 10);
    setField({ ...field, value: Number.isNaN(num) ? 0 : Math.max(0, num) });
  };

  const handleToggleUnlimited = (field: NumberField, setField: (v: NumberField) => void) => {
    setField({ ...field, unlimited: !field.unlimited });
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      await SwalAlert.warning('Validación', 'El nombre del plan es requerido.');
      return;
    }

    setSaving(true);
    const saved: DynamicPlan = {
      id: plan?.id ?? generateId(),
      name: trimmedName,
      priceMonthly,
      priceAnnual,
      trialDays,
      storageGB,
      locutores: serializeNumberField(locutores),
      programs: serializeNumberField(programs),
      events: serializeNumberField(events),
      lives: serializeNumberField(lives),
      notifications: serializeNumberField(notifications),
      frequencyPreferred,
      verification,
      customDomain,
      apps,
      active,
      visible,
      color,
      createdAt: plan?.createdAt ?? new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
    };

    onSave(saved);
    setSaving(false);
    await SwalAlert.toast(isEditing ? 'Plan actualizado.' : 'Plan creado.', 'success');
  };

  const renderNumberField = (
    label: string,
    field: NumberField,
    setField: (v: NumberField) => void,
    icon: string,
  ) => (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
        <i className={`${icon} text-foreground-400 text-xs`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <label className="block text-foreground-500 text-[10px] font-semibold">{label}</label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="number"
            min={0}
            disabled={field.unlimited}
            value={field.unlimited ? '' : field.value}
            onChange={(e) => handleNumberChange(field, setField, e.target.value)}
            className={`flex-1 min-w-0 px-2.5 py-1.5 bg-background-100 border border-background-200 rounded-lg text-xs font-mono text-foreground-800 outline-none focus:border-foreground-300 transition-colors ${field.unlimited ? 'opacity-40 cursor-not-allowed' : ''}`}
          />
          <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
            <button
              onClick={() => handleToggleUnlimited(field, setField)}
              className={`w-7 h-4 rounded-full transition-colors relative ${field.unlimited ? 'bg-emerald-500' : 'bg-background-300'}`}
            >
              <span
                className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all"
                style={{ left: field.unlimited ? '1rem' : '0.125rem' }}
              ></span>
            </button>
            <span className="text-foreground-400 text-[10px] whitespace-nowrap">Ilimitado</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderToggle = (
    label: string,
    icon: string,
    value: boolean,
    setValue: (v: boolean) => void,
    activeColor: string,
  ) => (
    <button
      onClick={() => setValue(!value)}
      className={`flex items-center gap-2 p-2.5 rounded-lg transition-all cursor-pointer text-left w-full ${
        value
          ? `bg-emerald-50 border border-emerald-200`
          : 'bg-background-100 border border-background-200 hover:border-background-300'
      }`}
    >
      <i className={`${icon} text-xs ${value ? 'text-emerald-600' : 'text-foreground-300'}`}></i>
      <span className={`text-[10px] font-medium flex-1 ${value ? 'text-emerald-700' : 'text-foreground-400'}`}>
        {label}
      </span>
      {value && <i className="ri-check-line text-emerald-500 text-xs"></i>}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">
              {isEditing ? 'Editar plan' : 'Crear nuevo plan'}
            </h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">
              {isEditing ? `Editando ${plan?.name}` : 'Configurá nombre, precios, límites y features'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-6">
          {/* General section */}
          <div className="space-y-4">
            <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">General</h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Nombre del plan</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Plan Básico, Empresarial, Oro..."
                  maxLength={30}
                  className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Color identificador</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {colorOptions.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setColor(c.value)}
                      className={`w-8 h-8 rounded-lg cursor-pointer transition-all flex items-center justify-center ${
                        color === c.value ? 'ring-2 ring-offset-2 ' + c.ring : 'ring-1 ring-background-200'
                      }`}
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                    >
                      {color === c.value && <i className="ri-check-line text-white text-xs"></i>}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Precio mensual (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
                  <input
                    type="number"
                    min={0}
                    max={99999}
                    value={priceMonthly}
                    onChange={(e) => setPriceMonthly(parseInt(e.target.value, 10) || 0)}
                    className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono font-bold text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                  />
                </div>
                <p className="text-foreground-300 text-[9px] mt-0.5">0 = Plan gratuito</p>
              </div>
              <div>
                <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Licencia anual (USD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
                  <input
                    type="number"
                    min={0}
                    max={999999}
                    value={priceAnnual}
                    onChange={(e) => setPriceAnnual(parseInt(e.target.value, 10) || 0)}
                    className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-xl text-xs font-mono font-bold text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                  />
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-foreground-300 text-[9px]">0 = Sin licencia anual</p>
                  <button
                    onClick={autoAnnual}
                    className="text-foreground-400 text-[9px] hover:text-foreground-600 transition-colors cursor-pointer underline"
                  >
                    Auto 10 meses
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold mb-1.5">Período de prueba (días)</label>
              <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                {[
                  { label: 'Sin prueba', value: 0 },
                  { label: '3 días', value: 3 },
                  { label: '7 días', value: 7 },
                  { label: '14 días', value: 14 },
                  { label: '30 días', value: 30 },
                  { label: '60 días', value: 60 },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTrialDays(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      trialDays === opt.value
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {priceMonthly > 0 && trialDays > 0 && (
                <p className="text-accent-600 text-[9px] mt-1 flex items-center gap-1">
                  <i className="ri-timer-line text-[9px]"></i>
                  Las radios podrán probar <strong>{trialDays} días</strong> gratis antes de pagar ${priceMonthly}/mes
                </p>
              )}
              {priceMonthly === 0 && trialDays > 0 && (
                <p className="text-amber-600 text-[9px] mt-1 flex items-center gap-1">
                  <i className="ri-error-warning-line text-[9px]"></i>
                  Este plan es gratuito. El trial no aplica.
                </p>
              )}
            </div>
          </div>

          {/* Storage section */}
          <div className="space-y-3">
            <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Almacenamiento</h4>
            <div className="flex flex-wrap items-center gap-1.5">
              {storagePresets.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleStoragePreset(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    storageGB === opt.value
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              <button
                onClick={() => setShowCustomStorage(!showCustomStorage)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  showCustomStorage
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'
                }`}
              >
                <i className="ri-add-line text-[9px] mr-0.5"></i>
                Personalizado
              </button>
            </div>
            {showCustomStorage && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={customStorage}
                  onChange={(e) => setCustomStorage(e.target.value)}
                  placeholder="GB"
                  className="flex-1 px-3 py-1.5 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomStorage()}
                />
                <button
                  onClick={handleCustomStorage}
                  className="px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-semibold cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
                >
                  Aplicar
                </button>
              </div>
            )}
            <p className="text-foreground-400 text-[10px] font-mono">
              {storageGB >= 1000 ? `${(storageGB / 1024).toFixed(1)} TB` : `${storageGB} GB`}
            </p>
          </div>

          {/* Limits section */}
          <div className="space-y-3">
            <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Límites</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderNumberField('Locutores', locutores, setLocutores, 'ri-mic-line')}
              {renderNumberField('Programas', programs, setPrograms, 'ri-calendar-schedule-line')}
              {renderNumberField('Eventos', events, setEvents, 'ri-calendar-event-line')}
              {renderNumberField('Lives', lives, setLives, 'ri-live-line')}
              {renderNumberField('Notificaciones', notifications, setNotifications, 'ri-notification-3-line')}
            </div>
          </div>

          {/* Features section */}
          <div className="space-y-3">
            <h4 className="text-foreground-700 text-[10px] font-bold uppercase tracking-wider">Features</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {renderToggle('Frecuencia preferida', 'ri-radar-line', frequencyPreferred, setFrequencyPreferred, 'sky')}
              {renderToggle('Verificación', 'ri-shield-check-line', verification, setVerification, 'emerald')}
              {renderToggle('Dominio propio', 'ri-global-line', customDomain, setCustomDomain, 'violet')}
              {renderToggle('Apps', 'ri-smartphone-line', apps, setApps, 'rose')}
            </div>
          </div>

          {/* Status toggles */}
          <div className="flex items-center gap-4 pt-2 border-t border-background-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <button
                onClick={() => setActive(!active)}
                className={`w-9 h-5 rounded-full transition-colors relative ${active ? 'bg-emerald-500' : 'bg-background-300'}`}
              >
                <span
                  className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                  style={{ left: active ? '1.125rem' : '0.125rem' }}
                ></span>
              </button>
              <span className="text-foreground-600 text-[10px] font-medium">
                {active ? 'Activo' : 'Inactivo'}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <button
                onClick={() => setVisible(!visible)}
                className={`w-9 h-5 rounded-full transition-colors relative ${visible ? 'bg-sky-500' : 'bg-background-300'}`}
              >
                <span
                  className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                  style={{ left: visible ? '1.125rem' : '0.125rem' }}
                ></span>
              </button>
              <span className="text-foreground-600 text-[10px] font-medium">
                {visible ? 'Visible (público)' : 'Oculto'}
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className={`flex-1 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              name.trim() && !saving
                ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                : 'bg-background-200 text-foreground-300 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin text-xs"></i>
                Guardando...
              </span>
            ) : (
              <>
                <i className={`${isEditing ? 'ri-save-line' : 'ri-add-line'} text-xs mr-1`}></i>
                {isEditing ? 'Guardar cambios' : 'Crear plan'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}